import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Sparkles } from "lucide-react";

const DISMISSED_KEY = "vibecheck_pwa_dismissed_v1";

/**
 * Native install banner for installable browsers (Chrome/Edge/Android).
 * Catches the `beforeinstallprompt` event, suppresses the browser's default
 * mini-bar, and shows our themed banner instead.
 *
 * iOS Safari doesn't fire this event — for iOS we show a small helper
 * sheet ("Tap Share → Add to Home Screen") on first non-standalone visit.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(DISMISSED_KEY)) return;

    // Already installed (running as standalone PWA) → do nothing
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Wait a moment so we don't slap the user the instant they land
      setTimeout(() => setShow(true), 6000);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
      localStorage.setItem(DISMISSED_KEY, "installed");
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS: no beforeinstallprompt — show our manual hint after a delay
    const ua = window.navigator.userAgent || "";
    const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIos && isSafari) {
      const t = setTimeout(() => setIosHint(true), 9000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
        clearTimeout(t);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "user");
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      // Some browsers throw if prompt() is called twice or out of a user
      // gesture window — guard with try/catch and dismiss gracefully.
      await deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "dismissed") {
        localStorage.setItem(DISMISSED_KEY, "user");
      } else if (choice?.outcome === "accepted") {
        localStorage.setItem(DISMISSED_KEY, "installed");
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VibeCheck] install prompt failed:", err);
    } finally {
      setShow(false);
      setDeferred(null);
    }
  };

  return (
    <AnimatePresence>
      {show && deferred && (
        <Banner key="install" onDismiss={dismiss} testid="pwa-install-banner">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-[#ff4fd8] to-[#7B61FF] flex items-center justify-center shadow-[0_0_24px_rgba(255,79,216,0.5)]">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-white text-sm truncate">
                Install VibeCheck
              </div>
              <div className="text-xs text-[#C9C7D9] truncate">
                One tap. No app store. Just a 🔥 home-screen icon.
              </div>
            </div>
          </div>
          <button
            onClick={install}
            data-testid="pwa-install-button"
            className="rounded-full px-4 py-2 text-sm font-bold text-white bg-gradient-to-r from-[#ff4fd8] to-[#7B61FF] shadow-[0_0_24px_rgba(255,79,216,0.45)] active:scale-95 inline-flex items-center gap-1.5 flex-shrink-0"
          >
            <Download className="w-4 h-4" /> Install
          </button>
        </Banner>
      )}

      {iosHint && !show && (
        <Banner key="ios" onDismiss={dismiss} testid="pwa-ios-hint">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-[#ff4fd8] to-[#7B61FF] flex items-center justify-center shadow-[0_0_24px_rgba(255,79,216,0.5)]">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-white text-sm">
                Add VibeCheck to your home screen
              </div>
              <div className="text-xs text-[#C9C7D9]">
                Tap <b className="text-white">Share</b> →{" "}
                <b className="text-white">Add to Home Screen</b>
              </div>
            </div>
          </div>
        </Banner>
      )}
    </AnimatePresence>
  );
}

function Banner({ children, onDismiss, testid }) {
  return (
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 80, opacity: 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 32 }}
      className="fixed z-[2147483647] pointer-events-auto"
      style={{
        // Anchor to LEFT side with safe margin from right edge so it can never
        // overlap the bottom-right watermark. Lift well above the watermark
        // and the bottom tab bar (~200px clears both with breathing room).
        left: "max(env(safe-area-inset-left, 0px), 12px)",
        right: "auto",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 200px)",
        // Cap width so it never reaches the watermark zone on the right.
        // Watermark is ~210px wide → reserve 220px on the right at all times.
        width: "min(440px, calc(100vw - 24px - 220px))",
        // Fallback for phones narrower than ~360px where the cap would be too tight
        minWidth: "min(280px, calc(100vw - 24px))",
      }}
      data-testid={testid}
    >
      <div
        className="flex items-center gap-3 rounded-2xl border border-white/15 px-3 py-3 shadow-[0_30px_60px_rgba(0,0,0,0.45),0_0_28px_rgba(255,79,216,0.18)]"
        style={{
          background:
            "linear-gradient(135deg, rgba(20, 12, 32, 0.92), rgba(11, 6, 20, 0.94))",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
        }}
      >
        {children}
        <button
          onClick={onDismiss}
          aria-label="Dismiss install prompt"
          data-testid="pwa-dismiss-button"
          className="flex-shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center text-[#9B98AE] hover:text-white hover:bg-white/8 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
}
